import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import { DashboardSummary } from '../../core/models/portal.models';

@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  imports: [RouterLink],
  template: `
    <div class="dashboard-page">
      @if (summary(); as item) {
        <section class="dashboard-hero">
          <div class="dashboard-hero-copy">
            <span class="kicker">DriveMe Academy Schulungsportal</span>
            <h1>Dashboard</h1>
            <p>Überblick über Fahrer, Schulungen und offene Maßnahmen.</p>

            <div class="dashboard-hero-meta">
              <span>{{ item.totalCompanies }} Unternehmer</span>
              <span>{{ item.totalDrivers }} Fahrer</span>
              <span>{{ item.activeEnrollments }} aktive Zuweisungen</span>
              <span>{{ item.retrainingDrivers }} Nachschulungen</span>
            </div>
          </div>

          <div class="dashboard-hero-tiles">
            <a class="dashboard-hero-tile dashboard-action-card" routerLink="/drivers" [queryParams]="{ status: 'READY' }">
              <strong>{{ item.readyDrivers }}</strong>
              <span>Einsatzbereit</span>
              <small>Fahrer ohne akuten Handlungsbedarf</small>
            </a>
            <a class="dashboard-hero-tile dashboard-action-card danger" routerLink="/drivers" [queryParams]="{ status: 'BLOCKED' }">
              <strong>{{ item.blockedDrivers }}</strong>
              <span>Gesperrt</span>
              <small>Benötigen sofortige Prüfung</small>
            </a>
          </div>
        </section>

        <section class="kpi-grid dashboard-stat-grid">
          <a class="dashboard-stat-card dashboard-action-card accent" routerLink="/companies">
            <span>Unternehmer</span>
            <strong class="kpi-value">{{ item.totalCompanies }}</strong>
            <small>Angeschlossene Betriebe im Portal</small>
          </a>
          <a class="dashboard-stat-card dashboard-action-card" routerLink="/drivers">
            <span>Fahrer gesamt</span>
            <strong class="kpi-value">{{ item.totalDrivers }}</strong>
            <small>Aktiv verwaltete Fahrerprofile</small>
          </a>
          <a class="dashboard-stat-card dashboard-action-card" routerLink="/drivers" [queryParams]="{ status: 'READY' }">
            <span>Einsatzbereit</span>
            <strong class="kpi-value">{{ item.readyDrivers }}</strong>
            <small>Freigegebene und einsatzfähige Fahrer</small>
          </a>
          <a class="dashboard-stat-card dashboard-action-card" routerLink="/drivers" [queryParams]="{ status: 'RETRAINING_REQUIRED' }">
            <span>Nachschulung</span>
            <strong class="kpi-value">{{ item.retrainingDrivers }}</strong>
            <small>Fahrer mit offenem Schulungsbedarf</small>
          </a>
          <a class="dashboard-stat-card dashboard-action-card danger" routerLink="/drivers" [queryParams]="{ status: 'BLOCKED' }">
            <span>Gesperrt</span>
            <strong class="kpi-value">{{ item.blockedDrivers }}</strong>
            <small>Aktuell nicht einsetzbare Fahrer</small>
          </a>
          <a class="dashboard-stat-card dashboard-action-card" routerLink="/drivers">
            <span>Aktive Zuweisungen</span>
            <strong class="kpi-value">{{ item.activeEnrollments }}</strong>
            <small>Laufende Kurs- und Lernzuweisungen</small>
          </a>
          <a class="dashboard-stat-card dashboard-action-card" routerLink="/drivers">
            <span>Abgeschlossen</span>
            <strong class="kpi-value">{{ item.completedEnrollments }}</strong>
            <small>Bereits erfolgreich beendete Vorgänge</small>
          </a>
        </section>

        @if (item.companies.length) {
          <section class="panel section-stack dashboard-company-panel">
            <div class="panel-heading">
              <div>
                <h2>Unternehmensübersicht</h2>
                <p>Angeschlossene Unternehmer und deren aktueller Schulungsstand.</p>
              </div>
              <div class="badge">{{ item.companies.length }} Unternehmen</div>
            </div>

            <table class="table dashboard-company-table">
              <thead>
                <tr>
                  <th>Unternehmer</th>
                  <th>Fahrer</th>
                  <th>Ø Fortschritt</th>
                  <th>Ø Scoring</th>
                  <th>Kritisch/offen</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                @for (company of item.companies; track company.id) {
                  <tr>
                    <td>
                      <a class="dashboard-company-link" [routerLink]="['/companies', company.id]">{{ company.name }}</a><br>
                      <span>{{ company.organizationName }}</span>
                    </td>
                    <td>{{ company.driverCount }}</td>
                    <td>{{ company.averageProgressPercent }} %</td>
                    <td>{{ company.averageScorePercent === null ? '-' : company.averageScorePercent + ' %' }}</td>
                    <td>
                      <strong>{{ company.criticalDrivers }}</strong> kritisch<br>
                      <span>{{ company.openEnrollments }} offen</span>
                    </td>
                    <td><span class="badge">{{ company.status }}</span></td>
                  </tr>
                }
              </tbody>
            </table>
          </section>
        } @else {
          <div class="empty">Es liegen noch keine Unternehmensdaten vor.</div>
        }
      } @else {
        <div class="empty">Dashboard wird geladen.</div>
      }
    </div>
  `
})
export class DashboardPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  protected readonly summary = signal<DashboardSummary | null>(null);

  ngOnInit(): void {
    this.api.dashboard().subscribe((summary) => this.summary.set(summary));
  }
}
